import React from 'react';

import { usePageStore } from '@client/store/pageStore/context/pageContext';
import i18n from '@client/utils/i18n';
import lediumApi from '@client/utils/lediumApi';
import { SpectrogramMapItem } from '@ledium/types';
import { AutocompleteBordered, AutocompleteOptionWithChart } from '@ledium/ui-common';
import { Grid } from '@mui/material';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

interface GrowthRecipeItem {
    id: string;
    name: string;
    description?: string;
}

const ProjectEditorSelectRecipe = () => {
    const {
        pageState: { formData },
        pageActions
    } = usePageStore();
    const [recipeList, setRecipeList] = React.useState<GrowthRecipeItem[]>([]);
    const [recipe, setRecipe] = React.useState<Partial<GrowthRecipeItem>>({});

    const onSelectRecipe = async (recipeId: string | null) => {
        const recipeLightRecipes = await lediumApi.get<string[]>(`growthRecipes/getUniqueLightRecipes/${recipeId}`);
        if (!recipeLightRecipes) {
            return;
        }
        const spectrogramMap = recipeLightRecipes.map((lightRecipeId) => ({
            lightRecipeId: lightRecipeId,
            spectrumMixId:
                (formData.spectrogramMap as SpectrogramMapItem[])
                    .filter((spectrumMixes) => spectrumMixes.lightRecipeId === lightRecipeId)
                    .map((spectrumMixItem) => spectrumMixItem.spectrumMixId)
                    .pop() || null
        }));
        pageActions.formData.updateFormItem({ recipeId, spectrogramMap });
    };

    React.useEffect(() => {
        if (formData.readOnly) {
            lediumApi.get<GrowthRecipeItem>(`growthRecipes/${formData.recipeId}`).then((data) => data && setRecipe(data));
        } else {
            lediumApi.get<GrowthRecipeItem[]>('growthRecipes').then((data) => data && setRecipeList(data));
        }
    }, [formData.readOnly, formData.recipeId]);

    function getOptionLabel(option: GrowthRecipeItem) {
        return option.name + (option.description ? ` (${option.description})` : '');
    }

    function isOptionEqualToValue(option: GrowthRecipeItem, value: GrowthRecipeItem) {
        return option.id === value.id;
    }

    async function onChange(_event: React.SyntheticEvent, item: GrowthRecipeItem | null) {
        await onSelectRecipe(item ? item.id : null);
    }

    function renderInput(params: object) {
        return <TextField {...(params as object)} label={i18n('select_recipe')} variant="outlined" />;
    }

    function renderOption(props: React.HTMLAttributes<HTMLLIElement>, option: GrowthRecipeItem) {
        return (
            <li {...props}>
                <AutocompleteOptionWithChart option={option} spectrogramData={null} size="small" />
            </li>
        );
    }

    let recipeName: string | React.ReactNode = '';
    if (formData.recipeId) {
        recipeName = recipe.name;
        if (recipe.description) {
            recipeName = `${recipeName} (${recipe.description})`;
        }
    } else {
        recipeName = i18n('no_recipe_selected');
    }

    return (
        <Grid item xs={12}>
            {formData.readOnly ? (
                <>
                    <Typography variant="subtitle2">Recipe: </Typography>
                    <span>{recipeName}</span>
                </>
            ) : (
                <AutocompleteBordered
                    value={recipeList.find((i) => i.id === formData.recipeId) || null}
                    options={recipeList}
                    getOptionLabel={getOptionLabel}
                    isOptionEqualToValue={isOptionEqualToValue}
                    onChange={onChange}
                    renderInput={renderInput}
                    renderOption={renderOption}
                />
            )}
        </Grid>
    );
};

export default ProjectEditorSelectRecipe;
